/* ============================================================
   KP CHAT — storefront assistant UI. Talks to /agent/chat (Cloudflare Worker,
   storefront-agent/kp-agent-worker.js). The Worker holds the Anthropic key;
   this file never sees it.

   COST AND ABUSE. Nothing is built, and no network connection opens, until a
   real person clicks the bubble. Automation is refused up front (webdriver,
   zero-size screen, no touch and no pointer) using the same signals as
   kp-analytics-beacon.js, so crawlers cannot spend API credit. The Worker
   enforces the real limits — 20/hour and 60/day per IP, 1,500/day store-wide.

   WHEN THE MODEL IS UNAVAILABLE. Out of credit, provider outage or throttling
   all arrive as a `fallback` event. The customer is never shown an API error:
   they get WhatsApp and email instead, so the conversation still converts.

   LOOK. Inherits --font-body-family (Manrope) and the theme's own colours, so
   it reads as part of the shop rather than a bolted-on widget. Full-height
   sheet on mobile, floating panel on desktop.

   PLACEMENT. Hidden on /cart and /checkout by the snippet, and hidden by CSS
   whenever the cart drawer is open (body.kp-drawer-open) so it can never sit
   over the checkout buttons.
   ============================================================ */
(function () {
  var ENDPOINT = '/agent/chat';
  var WHATSAPP = '447441395503';
  var SKEY = 'kp_chat_v2';   /* v2: abandons pre-context conversations */
  var MAX_KEEP = 20;
  var KP_LOGO = '<svg viewBox="0 0 96 96" aria-hidden="true"><rect width="96" height="96" rx="24" fill="#fff"/><g fill="#10213F" transform="translate(15.4,19.1) scale(0.622) translate(0,-1.425)"><path d="M 13.205 69.825 L 0 69.825 L 0.095 1.425 L 13.205 1.425 L 13.205 43.225 L 30.59 18.525 L 46.55 18.525 L 28.025 44.175 L 47.785 69.825 L 30.97 69.825 L 13.205 45.125 L 13.205 69.825 Z"/><g transform="translate(-170,0)"><path d="M 239.02 92.625 L 226.005 92.625 L 226.005 18.525 L 237.405 18.525 L 237.405 53.675 L 239.02 53.675 L 239.02 92.625 Z"/><path d="M 251.275 71.25 Q 243.627 71.25 238.497 67.688 Q 233.367 64.125 230.779 57.998 Q 228.19 51.87 228.19 44.175 Q 228.19 36.48 230.755 30.353 Q 233.32 24.225 238.307 20.663 Q 243.295 17.1 250.61 17.1 Q 257.972 17.1 263.435 20.615 Q 268.898 24.13 271.914 30.234 Q 274.93 36.338 274.93 44.175 Q 274.93 51.87 271.961 57.998 Q 268.992 64.125 263.672 67.688 Q 258.352 71.25 251.275 71.25 Z M 249.185 59.755 Q 253.365 59.755 256.025 57.665 Q 258.685 55.575 259.967 52.036 Q 261.25 48.498 261.25 44.175 Q 261.25 39.9 259.92 36.361 Q 258.59 32.823 255.811 30.709 Q 253.032 28.595 248.71 28.595 Q 244.672 28.595 242.179 30.543 Q 239.685 32.49 238.545 36.005 Q 237.405 39.52 237.405 44.175 Q 237.405 48.83 238.545 52.345 Q 239.685 55.86 242.274 57.808 Q 244.862 59.755 249.185 59.755 Z"/></g></g></svg>';

  /* ── automation gate ──────────────────────────────────────
     Cheap, conservative checks. A false positive costs one lost chat; a false
     negative costs API credit on a crawler. */
  function isAutomated() {
    try {
      if (navigator.webdriver) return true;
      if (!window.screen || window.screen.width === 0 || window.screen.height === 0) return true;
      if (!('ontouchstart' in window) && !window.matchMedia('(pointer:fine)').matches) return true;
      if (!navigator.languages || !navigator.languages.length) return true;
    } catch (e) { /* an old browser failing these is not a bot */ }
    return false;
  }

  var state = load();
  var els = null;
  var busy = false;

  function storeRoot() {
    var value = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return value.charAt(value.length - 1) === '/' ? value : value + '/';
  }
  function storeUrl(value) {
    if (!value) return value;
    try {
      var parsed = new URL(value, window.location.origin);
      if (parsed.origin !== window.location.origin) return value;
      var path = parsed.pathname + parsed.search + parsed.hash;
      var root = storeRoot();
      if (root !== '/' && path.indexOf(root) === 0) return path;
      return root + path.replace(/^\//, '');
    } catch (e) {
      return storeRoot() + String(value).replace(/^\//, '');
    }
  }
  function fetchWithTimeout(url, options, timeout) {
    var controller = new AbortController();
    var timer = window.setTimeout(function () { controller.abort(); }, timeout || 12000);
    return fetch(url, Object.assign({}, options, { signal: controller.signal }))
      .finally(function () { window.clearTimeout(timer); });
  }

  function load() {
    try { return JSON.parse(sessionStorage.getItem(SKEY)) || { history: [], cartId: null, checkoutUrl: null }; }
    catch (e) { return { history: [], cartId: null, checkoutUrl: null }; }
  }
  function save() {
    try {
      sessionStorage.setItem(SKEY, JSON.stringify({
        history: state.history.slice(-MAX_KEEP), cartId: state.cartId, checkoutUrl: state.checkoutUrl
      }));
    } catch (e) { /* private mode — the chat just won't persist */ }
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;   /* never innerHTML — model output is untrusted */
    return n;
  }

  /* ── page-aware opening line ──────────────────────────────
     Reads the data attributes theme.liquid already sets. A prompt that speaks
     to the page beats a generic "How can I help?", which everyone ignores. */
  function context() {
    var b = document.body, d = (b && b.dataset) || {};
    var type = d.pageType || '';
    var title = (document.querySelector('h1') || {}).textContent || '';
    title = title.trim().replace(/\s+/g, ' ').slice(0, 70);

    if (type === 'product' && title) {
      return {
        greet: 'Ask me anything about the ' + title + ' — sizing, delivery, or how it compares.',
        chips: [],                       /* fetched from /agent/hints for this product's range */
        productId: d.productId || null
      };
    }
    if (type === 'collection' && title) {
      /* Collection chips are safe: the visitor chose this category, so
         category-scoped prompts match their intent by construction. */
      return {
        greet: 'I can help you narrow down ' + title + '. Tell me the problem you are solving.',
        chips: ['Help me choose', 'What should I look for?', 'Delivery times']
      };
    }
    /* Homepage and everything else: greeting only, NO chips. The page tells
       us nothing about intent, so any chip is a guess that misdirects most
       visitors — someone here for fragrance should not be offered hip-pain
       pillows. Let them type; the assistant routes from what they say. */
    return { greet: 'Hi, how can we help?', chips: [] };
  }

  function build() {
    if (els) return els;
    var ctx = context();
    var wrap = el('div', 'kpc');
    wrap.innerHTML =
      '<div class="kpc-panel" role="dialog" aria-label="Shopping assistant" aria-modal="false" hidden>' +
        '<div class="kpc-head">' +
          '<span class="kpc-avatar-wrap" aria-hidden="true">' +
            '<span class="kpc-avatar">' + KP_LOGO + '</span>' +
          '</span>' +
          '<span class="kpc-headtext">' +
            '<span class="kpc-title">Ask us</span>' +
            /* Saying it is AI up front costs nothing and stops the "am I talking
               to a robot" resentment later. Naming WhatsApp in the same breath
               means nobody feels trapped with a bot. */
            '<span class="kpc-sub">AI assistant &middot; a person is on WhatsApp</span>' +
          '</span>' +
          '<button class="kpc-restart" type="button" aria-label="Start a new conversation" title="Start again">&#8635;</button>' +
          '<button class="kpc-x" type="button" aria-label="Close chat">&times;</button>' +
        '</div>' +
        '<div class="kpc-log" role="log" aria-live="polite"></div>' +
        '<div class="kpc-cart" hidden></div>' +
        '<div class="kpc-foot">' +
        '<form class="kpc-form">' +
          '<input class="kpc-in" type="text" autocomplete="off" enterkeyhint="send" ' +
            'placeholder="Ask a question…" maxlength="2000" aria-label="Your message">' +
          '<button class="kpc-send" type="submit" aria-label="Send message">' +
            '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">' +
            '<path fill="currentColor" d="M3 20.5v-6l8-2.5-8-2.5v-6L22 12z"/></svg>' +
          '</button>' +
        '</form>' +
        '<a class="kpc-wa" target="_blank" rel="noopener" ' +
          'href="https://wa.me/' + WHATSAPP + '?text=' + encodeURIComponent('Hello, I have a question') + '">' +
          '<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path fill="currentColor" ' +
          'd="M12 2a10 10 0 00-8.6 15L2 22l5.2-1.4A10 10 0 1012 2zm5.8 14.2c-.2.7-1.4 1.3-2 1.4-.5.1-1.1.1-1.8-.1a12 12 0 01-6.5-5.7c-.5-.8-.8-1.7-.8-2.5 0-.9.5-1.4.7-1.6.2-.2.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 1.9c.1.2 0 .4-.1.5l-.4.5c-.1.2-.3.3-.1.6.5.8 1 1.4 1.7 2 .6.5 1.1.7 1.4.9.2.1.4.1.5-.1l.7-.8c.2-.2.3-.2.6-.1l1.8.9c.2.1.4.2.4.3.1.2.1.7-.1 1.3z"/></svg>' +
          '<span>Prefer a human? <strong>Message us on WhatsApp</strong></span>' +
        '</a>' +
        '</div>' +
      '</div>';
    document.body.appendChild(wrap);

    els = {
      wrap: wrap,
      panel: wrap.querySelector('.kpc-panel'),
      log: wrap.querySelector('.kpc-log'),
      chips: null,
      cart: wrap.querySelector('.kpc-cart'),
      form: wrap.querySelector('.kpc-form'),
      input: wrap.querySelector('.kpc-in')
    };

    wrap.querySelector('.kpc-x').addEventListener('click', close);
    /* Restart wipes the saved conversation and rebuilds with a fresh greeting.
       Refresh deliberately does NOT clear the chat (customers navigate pages
       mid-conversation), so without this button there was no way to start
       over short of closing the tab — which made testing maddening. */
    wrap.querySelector('.kpc-restart').addEventListener('click', function () {
      try { sessionStorage.removeItem(SKEY); } catch (e) {}
      state = { history: [], cartId: null, checkoutUrl: null };
      busy = false;
      wrap.remove();
      els = null;
      build();
      open();
    });
    els.form.addEventListener('submit', function (e) { e.preventDefault(); send(els.input.value); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !els.panel.hidden) close();
    });

    if (state.history.length) {
      /* The saved conversation is a mixed timeline: text turns, product cards,
         discount buttons. Restoring only the text (the old behaviour) meant a
         customer who navigated to another page kept the model's question but
         LOST the recommended products it referred to. Cards re-render with
         live Add-to-basket buttons, so an add still works after navigation. */
      state.history.forEach(function (m) {
        if (m.role === 'cards' && m.items) renderProducts(m.items);
        else if (m.role === 'discount' && m.code) renderDiscount(m);
        else if (m.role === 'user' || m.role === 'assistant') bubble(m.role, m.content);
      });
    } else {
      bubble('assistant', ctx.greet);
      setChips(ctx.chips);
      /* On a product page the right questions depend on the range — a pregnancy
         pillow must never be asked "is a mattress included". The Worker knows
         the template; ask it. */
      if (ctx.productId) {
        /* &v=N busts the hints edge cache when CHIPS copy changes.
           Bump this whenever storefront-agent/knowledge.js CHIPS change. */
        fetch('/agent/hints?product_id=' + encodeURIComponent(ctx.productId) + '&v=4')
          .then(function (r) { return r.json(); })
          .then(function (d) { if (!state.history.length && d.chips) setChips(d.chips); })
          .catch(function () { setChips(['Help me choose', 'When would it arrive?', 'Delivery and returns']); });
      }
    }
    if (state.checkoutUrl) showCart();
    return els;
  }

  /* ── MOBILE KEYBOARD HANDLING (10 Aug 2026) ─────────────────────────────
     The panel is fixed to the LAYOUT viewport (bottom:0, 88svh). When the
     on-screen keyboard opens, iOS Safari does not resize that viewport — it
     pans the VISUAL viewport to reveal the focused input. Result on a real
     iPhone: the panel slid half off-screen, the product page showed through
     above it, and the input row hid behind the keyboard. Two-part fix:

     1. No autofocus on touch devices. open() used to focus the input
        immediately, so the keyboard — and the broken state — appeared before
        the customer saw the panel at all. Desktop keeps the autofocus.
     2. While the panel is open on mobile, the body is scroll-locked
        (position:fixed; overflow:hidden alone is ignored by iOS) and a
        visualViewport listener pins the panel to exactly the visible area,
        so the input always sits just above the keyboard. */
  var savedScrollY = 0, vvBound = false;
  function vvSync() {
    if (!els || els.panel.hidden) return;
    if (!matchMedia('(max-width:600px)').matches) return;
    var vv = window.visualViewport;
    if (!vv) return;
    if (innerHeight - vv.height > 60) {          /* keyboard is up */
      els.panel.style.top = vv.offsetTop + 'px';
      els.panel.style.bottom = 'auto';
      els.panel.style.height = vv.height + 'px';
      els.panel.style.maxHeight = 'none';
      els.panel.style.borderRadius = '0';
    } else {                                     /* keyboard dismissed */
      els.panel.style.top = '';
      els.panel.style.bottom = '';
      els.panel.style.height = '';
      els.panel.style.maxHeight = '';
      els.panel.style.borderRadius = '';
    }
    els.log.scrollTop = els.log.scrollHeight;
  }
  function open() {
    build();
    /* Clear the dwell nudge — once they are in, the offer has been taken. */
    if (fab) fab.removeAttribute('data-notify');
    els.panel.hidden = false;
    document.body.classList.add('kpc-open');
    if (fab) fab.setAttribute('aria-expanded', 'true');
    if (matchMedia('(max-width:600px)').matches) {
      savedScrollY = window.scrollY || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = -savedScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      if (window.visualViewport && !vvBound) {
        visualViewport.addEventListener('resize', vvSync);
        visualViewport.addEventListener('scroll', vvSync);
        vvBound = true;
      }
    } else {
      els.input.focus({ preventScroll: true });
    }
    els.log.scrollTop = els.log.scrollHeight;
  }
  function close() {
    if (!els) return;
    els.panel.hidden = true;
    els.panel.style.top = '';
    els.panel.style.bottom = '';
    els.panel.style.height = '';
    els.panel.style.maxHeight = '';
    els.panel.style.borderRadius = '';
    document.body.classList.remove('kpc-open');
    if (document.body.style.position === 'fixed') {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, savedScrollY);
    }
    if (fab) { fab.setAttribute('aria-expanded', 'false'); fab.focus(); }
  }

  /* The model is told not to use markdown, but a stray **bold** should never
     reach the customer as literal asterisks. Rendered as real DOM nodes —
     never innerHTML, because this is model output. */
  function setRich(node, text) {
    node.textContent = '';
    String(text).split(/(\*\*[^*]+\*\*)/).forEach(function (part) {
      if (!part) return;
      if (part.slice(0, 2) === '**' && part.slice(-2) === '**') {
        var st = document.createElement('strong');
        st.textContent = part.slice(2, -2);
        node.appendChild(st);
      } else {
        node.appendChild(document.createTextNode(part.replace(/^\s*[-*]\s+/gm, '')));
      }
    });
  }

  function bubble(role, text) {
    var b = el('div', 'kpc-msg kpc-' + role);
    setRich(b, text);
    els.log.appendChild(b);
    els.log.scrollTop = els.log.scrollHeight;
    return b;
  }

  function setChips(list) {
    if (!els.chips) {
      els.chips = el('div', 'kpc-chips');
      els.log.appendChild(els.chips);
    }
    els.chips.textContent = '';
    (list || []).forEach(function (c) {
      var b = el('button', 'kpc-chip', c);
      b.type = 'button';
      b.addEventListener('click', function () { send(c); });
      els.chips.appendChild(b);
    });
  }

  /* Cards beat a paragraph of product names — a picture, a price and one tap.
     The add goes straight to the Worker rather than back through the model:
     deterministic, instant, and it costs no tokens. */
  function renderProducts(items) {
    var row = el('div', 'kpc-cards');
    items.forEach(function (p) {
      var card = el('div', 'kpc-card');
      if (p.image) {
        var img = el('img', 'kpc-card-img');
        img.src = p.image; img.alt = ''; img.loading = 'lazy';
        card.appendChild(img);
      }
      var body = el('div', 'kpc-card-body');
      body.appendChild(el('div', 'kpc-card-title', p.title));
      /* Star rating. Renders ONLY when the Worker sends a real p.rating, which
         it does not today — so nothing appears. Wire this to the Loox review
         data before switching it on: a hardcoded 4.9 per SKU is a fabricated
         endorsement, and the shop already has the genuine numbers. */
      if (typeof p.rating === 'number' && p.rating > 0) {
        var stars = el('div', 'kpc-card-stars');
        var full = Math.round(p.rating);
        stars.appendChild(el('span', 'kpc-card-stars-ico',
          '\u2605'.repeat(full) + '\u2606'.repeat(Math.max(0, 5 - full))));
        stars.appendChild(el('span', 'kpc-card-stars-num', p.rating.toFixed(1)));
        body.appendChild(stars);
      }
      if (p.price) body.appendChild(el('div', 'kpc-card-price', p.price));
      var acts = el('div', 'kpc-card-acts');

      /* ADD ONLY WHAT THE CUSTOMER ACTUALLY CHOSE.
         The Worker builds each card from p.variants[0], so a multi-variant
         product used to add its FIRST variant on tap. Wall beds are Colour x
         Mattress = 8 variants, so "Add to basket" silently put Grey Matt with
         no mattress in the cart on a £1,115 order, whatever the shopper had
         asked the chat about. Same for pillow colours and fragrance sizes.

         The authority is /products/<handle>.js — Shopify's own product JSON,
         same origin, browser-cached, and always current. MCP's variant list is
         not trusted for this: it can be partial, and being wrong here means a
         wrong physical item shipped.

         One variant -> add it, using the id from Shopify rather than MCP.
         More than one -> no add button at all, just a link to choose options.
         Unknown (fetch failed) -> treat as multi and link out. Failing to the
         product page costs a click; failing to the cart costs a return. */
      var handle = null;
      try {
        handle = String(p.url || '').split('/products/')[1];
        if (handle) handle = handle.split(/[?#]/)[0];
      } catch (e) { handle = null; }

      var chooseLink = function (label) {
        if (!p.url) return null;
        var a = el('a', 'kpc-add kpc-add--choose', label || 'Choose options');
        a.href = storeUrl(p.url);
        return a;
      };

      if (p.variant_id && handle) {
        var slot = el('span', 'kpc-add-slot');
        acts.appendChild(slot);
        var pending = el('span', 'kpc-add-wait', 'Checking options…');
        slot.appendChild(pending);

        fetchWithTimeout(storeRoot() + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' }, 12000)
          .then(function (r) { if (!r.ok) throw new Error('no product json'); return r.json(); })
          .then(function (pd) {
            var vs = (pd && pd.variants) || [];
            slot.textContent = '';
            if (vs.length === 1 && vs[0].available) {
              slot.appendChild(buildAdd(String(vs[0].id)));
            } else {
              var link = chooseLink(vs.length > 1 ? 'Choose options' : 'View product');
              if (link) slot.appendChild(link);
            }
          })
          .catch(function () {
            slot.textContent = '';
            var link = chooseLink('View product');
            if (link) slot.appendChild(link);
          });
      } else if (p.url) {
        var vp = chooseLink('View product');
        if (vp) acts.appendChild(vp);
      }

      function buildAdd(variantId) {
        var add = el('button', 'kpc-add', 'Add to basket');
        add.type = 'button';
        add.addEventListener('click', function () {
          /* The STORE cart, not an MCP cart. MCP's update_cart creates a second,
             separate cart — the header count would never move and the checkout
             link would contain only the chat item. Same endpoint the rest of the
             theme uses (see pillow-finder.js). */
          /* variantId, NOT p.variant_id: the argument is the id Shopify itself
             just returned for this handle. p.variant_id comes from MCP and is
             the thing that was adding the wrong item. */
          var numeric = String(variantId).replace(/^.*\//, '').replace(/\?.*$/, '');
          add.disabled = true; add.textContent = 'Adding…';
          fetchWithTimeout(storeRoot() + 'cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: numeric, quantity: 1 })
          }, 12000).then(function (r) {
            if (!r.ok) throw new Error('add failed');
            return r.json();
          }).then(function () {
            add.textContent = 'Added \u2713';
            /* Let the theme's own tracking see it, same shape as elsewhere. */
            document.dispatchEvent(new CustomEvent('kp:analytics', {
              detail: { event: 'cart_add_success', source: 'chat_agent',
                        variant_id: numeric, product_sku: p.sku || '', quantity: 1 }
            }));
            /* Make the add VISIBLE outside the chat. Without this the server
               cart updated but the header badge and drawer did not, so the
               add looked like it had failed. */
            fetchWithTimeout(storeRoot() + 'cart.js', {}, 12000).then(function (r) { return r.json(); }).then(function (c) {
              var n = c && c.item_count;
              if (typeof n !== 'number') return;
              document.querySelectorAll('.cart-count-badge').forEach(function (b) {
                b.textContent = n;
                b.style.display = n > 0 ? '' : 'none';
              });
            }).catch(function () {});
            document.dispatchEvent(new CustomEvent('kp:cart-refresh', { detail: { open: false } }));
            state.checkoutUrl = storeRoot() + 'cart';
            showCart();
            save();
          }).catch(function () { add.disabled = false; add.textContent = 'Add to basket'; });
        });
        /* Returned, not appended: the caller decides whether this button is
           allowed to exist at all, and drops it into the slot only after
           Shopify has confirmed the product has exactly one variant. */
        return add;
      }

      /* "View" is now redundant when the slot already renders a Choose options
         or View product link, so it only shows alongside a real add button. */
      if (p.url && p.variant_id && handle) {
        var view = el('a', 'kpc-view', 'View');
        view.href = storeUrl(p.url);
        acts.appendChild(view);
      }
      body.appendChild(acts);
      card.appendChild(body);
      row.appendChild(card);
    });
    els.log.appendChild(row);
    els.log.scrollTop = els.log.scrollHeight;
    return row;
  }

  /* Shopify's own /discount/<code> route applies the code to the session and
     redirects, so one tap does it — no copying, no typing it wrong at checkout. */
  function renderDiscount(d) {
    var wrap = el('div', 'kpc-promo');
    var a = el('a', 'kpc-promo-btn');
    a.href = storeRoot() + 'discount/' + encodeURIComponent(d.code)
      + '?redirect=' + encodeURIComponent(storeRoot() + 'cart');
    var promoRow = el('div', 'kpc-promo-row');
    promoRow.appendChild(el('span', 'kpc-promo-code', d.code));
    promoRow.appendChild(el('span', null, 'Apply ' + d.percent + '% off'));
    a.appendChild(promoRow);
    /* Replaces "Valid today only", which was false — OFF10 has no expiry.
       Whole-order scope is true AND worth more on an expensive basket. */
    a.appendChild(el('span', 'kpc-promo-expire', 'Applies to your whole order'));
    wrap.appendChild(a);
    els.log.appendChild(wrap);
    els.log.scrollTop = els.log.scrollHeight;
  }

  function showCart() {
    if (!state.checkoutUrl) return;
    els.cart.hidden = false;
    els.cart.textContent = '';
    var row = el('div', 'kpc-cartrow');
    var v = el('a', 'kpc-view-basket');
    v.href = storeRoot() + 'cart';
    v.appendChild(el('span', 'kpc-tick', '\u2713'));
    v.appendChild(el('span', null, 'In your basket'));
    /* Straight to Shopify checkout, where Shop Pay, Apple Pay, Google Pay and
       PayPal all appear. Payment never happens inside the chat. */
    var c = el('a', 'kpc-checkout', 'Checkout');
    c.href = storeRoot() + 'checkout';
    row.appendChild(v); row.appendChild(c);
    els.cart.appendChild(row);
    /* SHIPPING.GB.free_over in knowledge.js. Replaces "Order by 2pm for
       next-day delivery", which was true of no range and which this bar could
       not have scoped anyway — it does not know what is in the cart. Ranges
       that ship free regardless (furniture, artisan) are understated by it,
       never oversold. */
    var freeDelivery = window.KPChatConfig && window.KPChatConfig.freeDelivery;
    if (freeDelivery) els.cart.appendChild(el('p', 'kpc-cart-urgency', freeDelivery));
  }

  /* Shown when the model is unavailable — never an API error. */
  function offerHuman(text) {
    bubble('assistant', text);
    var row = el('div', 'kpc-escalate');
    var wa = el('a', 'kpc-wa-btn', 'WhatsApp us');
    /* "Fastest reply" replaces "Replies in ~5 min": no median has been
       measured, and that line would have shown at 3am too. */
    wa.appendChild(el('span', 'kpc-wa-reply', 'Fastest reply'));
    wa.href = 'https://wa.me/' + WHATSAPP;
    wa.target = '_blank'; wa.rel = 'noopener';
    var mail = el('a', 'kpc-mail-btn', 'Email us');
    mail.href = 'mailto:sale@kneepillow.co.uk';
    row.appendChild(wa); row.appendChild(mail);
    els.log.appendChild(row);
    els.log.scrollTop = els.log.scrollHeight;
  }

  function send(text) {
    text = (text || '').trim();
    if (!text || busy || isAutomated()) return;
    busy = true;
    if (els.chips) { els.chips.remove(); els.chips = null; }
    els.input.value = '';
    bubble('user', text);
    state.history.push({ role: 'user', content: text });

    var status = el('div', 'kpc-status', 'Thinking…');
    els.log.appendChild(status);
    els.log.scrollTop = els.log.scrollHeight;

    /* Bubbles are created in STREAM ORDER, not up front. The old code made
       one answer bubble before anything arrived; when cards landed first,
       the follow-up text streamed into a bubble ABOVE them while the log was
       auto-scrolled to the bottom — the customer never saw the answer without
       scrolling up. Now text arriving after cards opens a fresh bubble below
       the cards, so reading order always matches arrival order. */
    var out = null, curText = '';
    var cardsRow = null, cardsHistIdx = null;
    var acc = '', handled = false;
    function textInto(t) {
      acc += t;
      /* If something non-text (cards, discount) was appended after the current
         bubble, start a new bubble underneath it. */
      if (!out || els.log.lastElementChild !== out) {
        if (status.parentNode) els.log.appendChild(status); /* keep status at the tail */
        out = bubble('assistant', '');
        els.log.insertBefore(out, status);
        curText = '';
      }
      curText += t;
      setRich(out, curText);
    }
    /* Watchdog: if the stream produces nothing for 90s, abort — otherwise
       `busy` stays true and the input is frozen until the tab dies. */
    var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var watchdog = setTimeout(function () { if (ctrl) ctrl.abort(); }, 90000);
    fetch(ENDPOINT, {
      signal: ctrl ? ctrl.signal : undefined,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: text,
        history: state.history.slice(0, -1)
          .filter(function (m) { return m.role === 'user' || m.role === 'assistant'; })
          .slice(-MAX_KEEP),
        /* Page context so the model knows where the customer is standing.
           Without it, "Help me choose" on a pregnancy pillow page produced
           "are you shopping for pillows, furniture, fragrance or football?" —
           a question the page already answers. */
        context: (function () {
          /* Product AND collection pages. Collections were missed at first,
             so "Help me choose" on the fragrance collection still asked
             "pillows, furniture, fragrance or football?" — the exact failure
             this context exists to prevent. */
          var d = (document.body && document.body.dataset) || {};
          var t = d.pageType;
          /* Market prefix (18 Aug 2026): /en-eu/products/... means the
             customer sees EU stock and prices, and the assistant must answer
             from the same pool - without this a shopper on /en-eu/ asking
             "is this in stock?" was quoted UK stock. Sent even when the page
             has no usable title, because the market matters on its own. */
          var mkt = (location.pathname.match(/^\/(en-(?:us|ca|au|eu|nz|sg))\//) || [])[1];
          if (t !== 'product' && t !== 'collection') {
            return mkt ? { market: mkt } : undefined;
          }
          var h1 = document.querySelector('h1');
          var title = h1 ? h1.textContent.trim().replace(/\s+/g, ' ').slice(0, 120) : '';
          if (!title) return mkt ? { market: mkt } : undefined;
          return {
            page_type: t,
            product_id: t === 'product' ? (d.productId || undefined) : undefined,
            title: title,
            market: mkt || undefined
          };
        })()
      })
    }).then(function (r) {
      /* `if (out)` — out is null here in the normal case. It is only created
         by textInto() when the first token arrives, and on a 429 no token
         ever does, so the bare out.remove() threw a TypeError. handled had
         already been set to true one statement earlier, so the catch block
         below then declined to show the human fallback as well: a
         rate-limited customer got a completely blank reply. Same guarded
         shape as the fallback/error handlers further down. */
      if (r.status === 429) { handled = true; if (out) out.remove(); offerHuman('I have had a lot of questions in the last hour. Give me a little while, or speak to the team now:'); return; }
      if (!r.ok || !r.body) throw new Error('http ' + r.status);
      var reader = r.body.getReader(), dec = new TextDecoder(), buf = '';
      return (function pump() {
        return reader.read().then(function (res) {
          if (res.done) return;
          buf += dec.decode(res.value, { stream: true });
          var parts = buf.split('\n\n'); buf = parts.pop();
          parts.forEach(function (c) {
            var ev = (c.match(/^event: (.*)$/m) || [])[1];
            var dl = (c.match(/^data: (.*)$/m) || [])[1];
            if (!ev || !dl) return;
            var d; try { d = JSON.parse(dl); } catch (e) { return; }

            if (ev === 'text') { textInto(d.text); }
            else if (ev === 'tool') {
              status.textContent =
                d.name === 'search_catalog' ? 'Searching the catalogue…' :
                d.name === 'update_cart' ? 'Updating your basket…' :
                d.name === 'kp_delivery_and_stock' ? 'Checking stock and delivery…' :
                d.name === 'kp_search_knowledge' ? 'Reading our guides…' :
                d.name === 'kp_contact_team' ? 'Sending to the team…' : 'Looking that up…';
            }
            else if (ev === 'cart') {
              if (d.cartId) state.cartId = d.cartId;
              if (d.checkoutUrl) { state.checkoutUrl = d.checkoutUrl; showCart(); }
            }
            else if (ev === 'discount') {
              renderDiscount(d);
              state.history.push({ role: 'discount', code: d.code, percent: d.percent });
              if (status.parentNode) els.log.appendChild(status);
            }
            else if (ev === 'products') {
              if (d.items && d.items.length) {
                /* REPLACE, never accumulate, within one turn. The model often
                   searches several times while narrowing down; rendering every
                   search stacked 9-12 cards — stadium puzzles and a pillow
                   cover offered as perfume gifts. Only its final search
                   reflects what it actually recommends. */
                if (cardsRow) cardsRow.remove();
                cardsRow = renderProducts(d.items);
                if (cardsHistIdx !== null) state.history[cardsHistIdx] = { role: 'cards', items: d.items };
                else { state.history.push({ role: 'cards', items: d.items }); cardsHistIdx = state.history.length - 1; }
                if (status.parentNode) els.log.appendChild(status);
              }
            }
            else if (ev === 'fallback') { handled = true; if (out && !curText) out.remove(); offerHuman(d.text); }
            else if (ev === 'error' && !acc) { handled = true; if (out) out.remove(); offerHuman('Sorry — I could not answer that just now.'); }
            els.log.scrollTop = els.log.scrollHeight;
          });
          return pump();
        });
      })();
    }).catch(function () {
      if (!handled && !acc) { if (out) out.remove(); offerHuman('Sorry — I could not reach the shop just then.'); }
    }).finally(function () {
      clearTimeout(watchdog);
      status.remove();
      if (acc) state.history.push({ role: 'assistant', content: acc });
      save();
      busy = false;
    });
  }

  /* ── boot ─────────────────────────────────────────────────
     Only the button exists until someone clicks it. */
  var fab = null;
  /* PRIVATE PREVIEW MODE — the merchant tests on the live theme without any
     customer seeing the bubble. Visit ?kpchat=1 once to enable for this
     browser (persists across pages via localStorage), ?kpchat=0 to disable.
     When testing is signed off, set PREVIEW = false and the bubble shows for
     everyone. Nothing else needs to change to go live. */
  /* LIVE since 7 Aug 2026 (was true through testing). Set back to true to
     pull the widget from customers instantly without touching anything else;
     ?kpchat=1 still works for merchant preview either way. */
  var PREVIEW = false;
  function previewAllowed() {
    try {
      var q = new URLSearchParams(location.search).get('kpchat');
      if (q === '1') localStorage.setItem('kp_chat_preview', '1');
      if (q === '0') localStorage.removeItem('kp_chat_preview');
      return localStorage.getItem('kp_chat_preview') === '1';
    } catch (e) { return false; }
  }
  /* ── COLLISION MANAGER (7 Aug 2026) ──────────────────────────────────
     The launcher must NEVER cover a buying control. Three kinds exist here:
       fixed bars   — .kp-bottom-nav (mobile nav, z-130, absent on product and
                      cart pages) and .sticky-atc-bar.visible (product pages,
                      z-90). Both sit at bottom:0, exactly where the fab lives.
       in-flow      — the product form's Add-to-basket and Shopify's dynamic
                      checkout buttons (.shopify-payment-button), which scroll
                      past UNDER the fab on their way up the page. These are
                      sacred: "you can not remove dynamic checkout buttons".
     Strategy: on scroll/resize (rAF-throttled, passive), lift the fab by the
     tallest visible fixed bar, then — if an in-flow buy button would still
     intersect it — lift further so the fab rides ABOVE the button while it
     passes, dropping back once it has gone. The offset is one CSS variable
     consumed by both fab and panel, so they move together and the transition
     in the stylesheet keeps it smooth rather than teleporting. */
  /* LIFT only over pure NAVIGATION. The sticky ATC bar moved to the hide
     list on 7 Aug 2026: lifting clear of an ~90px bar pushed the launcher up
     into the product title and price (merchant screenshot on a fragrance PDP
     — "not to kill product info area"). It is also a buy control, so the same
     rule as Add-to-bag applies: while it is on screen the customer is in
     buying mode and the chat should get out of the way entirely. The bottom
     nav is only ~62px and is genuinely persistent, so hiding there would mean
     no chat for the whole visit — that one still lifts. */
  /* DESKTOP SPLIT (7 Aug 2026). The rule above is right for a phone and wrong
     for a desktop PDP, where it hid the launcher permanently — measured at
     1440x900: gone at scrollY 0 because Buy-with-Shop fills the bottom-right
     of the fold, and never returning afterwards because .sticky-atc-bar is a
     FULL-WIDTH box, so the horizontal overlap test matched forever.
     On a wide viewport there is a gutter beside the buy column (80px at 1024
     and 1280, 92px at 1440; 95px beside the sticky bar's own button), so:
        sticky ATC   -> LIFT above it, as with the bottom nav. The mobile
                        objection ("pushed the launcher into the product title
                        and price") was a narrow-viewport problem; at >=601px
                        the lifted pill sits over the far-right edge of body
                        content, which is where every chat widget floats.
        buy buttons  -> SHRINK to a 44px circle that fits the gutter, rather
                        than disappear. Hiding stays as the fallback if even
                        the mini would overlap.
     Mobile behaviour is deliberately untouched. */
  var OBSTACLE_FIXED = '.kp-bottom-nav';
  var STICKY_BAR = '.sticky-atc-bar.visible';
  var OBSTACLE_FLOW = 'form[action*="/cart/add"] [type="submit"], .shopify-payment-button, [data-shopify="payment-button"]';
  var MINI_W = 44, MINI_RIGHT = 10;
  var fabNatW = 0, fabNatH = 0;
  var liftTick = false, lastKey = '';
  function visible(e) {
    if (!e || !e.offsetParent && getComputedStyle(e).position !== 'fixed') return null;
    var r = e.getBoundingClientRect();
    return (r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < innerHeight) ? r : null;
  }
  /* Would a fab of this size, at this offset, land on any obstacle?
       fabRight is the CSS `right` value, so the fab's right edge is measured
       from documentElement.clientWidth — NOT innerWidth. innerWidth includes
       the scrollbar, which put the test box ~15px right of where the fab
       actually renders and quietly skewed every overlap decision. */
  function hits(sel, fabW, fabH, fabRight, bottom) {
    var vh = innerHeight;
    var top = vh - bottom - fabH;
    var rightEdge = document.documentElement.clientWidth - fabRight;
    var els = document.querySelectorAll(sel);
    for (var i = 0; i < els.length; i++) {
      var r = visible(els[i]);
      if (!r) continue;
      if (r.right > rightEdge - fabW - 8 && r.left < rightEdge + 8 &&
          r.bottom > top - 8 && r.top < top + fabH + 8) return true;
    }
    return false;
  }

  function updateLift() {
    liftTick = false;
    if (!fab) return;

    /* FORCED REFLOW GUARD (7 Aug 2026). Lighthouse attributed a recurring
       forced reflow to this file: the 800ms safety poll ran the full
       getBoundingClientRect sweep even on a page that had not moved, and
       reading element geometry after the previous tick's class/style writes
       forces the browser to re-lay-out.

       This signature is built ONLY from values that cost no layout —
       scrollY, innerWidth/innerHeight and a className string. clientWidth is
       deliberately NOT used here: it does force layout, which would defeat
       the whole guard. If nothing that can move the launcher has changed,
       return before touching any element geometry. On an idle page the poll
       is now free. */
    var bar = document.querySelector('.sticky-atc-bar');
    var key = (scrollY | 0) + '|' + innerWidth + '|' + innerHeight + '|' +
              (bar ? bar.className : '') + '|' +
              document.querySelectorAll(OBSTACLE_FIXED).length;
    if (key === lastKey) return;
    lastKey = key;

    var wide = matchMedia('(min-width:601px)').matches;
    var lift = 0, vh = innerHeight, i, r;

    /* PERSISTENT fixed bars: LIFT above the tallest. Hiding here would remove
       the chat for as long as the bar exists, which on nav pages is the whole
       visit — and on a PDP the sticky ATC bar is exactly that, on BOTH
       breakpoints. Mobile joined this list on 7 Aug 2026 for the same reason
       desktop did: the launcher was hidden for as long as the bar was up. */
    var bars = document.querySelectorAll(OBSTACLE_FIXED + ',' + STICKY_BAR);
    var liftedByBar = false;
    for (i = 0; i < bars.length; i++) {
      /* FIRST-LOAD OVERLAP FIX (10 Aug 2026). The sticky ATC bar slides in
         with a 300ms translateY transition. product-controller.js (deferred,
         earlier in theme.liquid) adds .visible BEFORE this script's first
         updateLift(), so the rect read here was mid-slide — still off-screen
         — and the bar was skipped. Worse, the change signature had already
         captured className WITH "visible", so when the slide finished nothing
         in the key had changed and the 800ms poll early-returned forever: the
         launcher sat on ADD TO BAG until the first scroll changed scrollY.
         For this bar the CLASS is the truth: once .visible is on it WILL
         occupy its layout height at the viewport bottom, so lift by
         offsetHeight (untransformed) and never trust the animating rect. */
      if (bars[i].matches && bars[i].matches(STICKY_BAR)) {
        var bh = bars[i].offsetHeight;
        if (bh > 0) { lift = Math.max(lift, bh + 8); liftedByBar = true; }
        continue;
      }
      r = visible(bars[i]);
      if (r && r.bottom >= vh - 4) {
        lift = Math.max(lift, Math.round(vh - r.top) + 8);
        liftedByBar = true;
      }
    }

    /* Cache the pill's natural size while it IS a pill. Reading it during the
       mini state returns 44 and the fab would then never test its way back to
       full size — it would latch mini for the rest of the visit. */
    if (!fab.classList.contains('kpc-mini') && !fab.classList.contains('kpc-dodge')) {
      var fr = fab.getBoundingClientRect();
      if (fr.width > 0) { fabNatW = fr.width; fabNatH = fr.height; }
    }
    var fabW = fabNatW || 112, fabH = fabNatH || 52;

    var bottom = 20 + lift;
    var mini = false, covered = false;

    /* On a narrow screen the lifted pill is what the merchant rejected — it
       covered the product title and price. Riding above the bar as a 44px
       circle is a fraction of that, so mobile shrinks whenever a bar lifted
       it. Desktop keeps the full pill there; it has the room. */
    if (!wide && liftedByBar) mini = true;

    if (hits(OBSTACLE_FLOW, fabW, fabH, 20, bottom)) {
      /* An in-flow buy button is under the launcher. Try the circle in the
         gutter beside the buy column before giving up on showing anything.
         On mobile these buttons span nearly the full width, so the mini has
         nowhere to go and this correctly falls through to hiding. */
      if (!hits(OBSTACLE_FLOW, MINI_W, MINI_W, MINI_RIGHT, bottom)) mini = true;
      else { mini = false; covered = true; }
    }

    fab.classList.toggle('kpc-mini', mini);
    fab.classList.toggle('kpc-dodge', covered);
    document.documentElement.style.setProperty('--kpc-off', lift + 'px');
  }
  function queueLift() {
    /* setTimeout, NOT requestAnimationFrame — rAF suspends in background and
       throttled tabs and the one-shot flag would jam TRUE forever there. */
    if (!liftTick) { liftTick = true; setTimeout(updateLift, 60); }
  }

  function init() {
    if (PREVIEW && !previewAllowed()) return;
    fab = el('button', 'kpc-fab');
    fab.type = 'button';
    /* WCAG 2.5.3 (label in name): the accessible name must CONTAIN the visible
       text, so a voice-control user saying "Ask us" actually activates it.
       "Ask a question" did not contain "Ask us" and broke that. Keeping the
       visible words first, then the extra context. */
    fab.setAttribute('aria-label', 'Ask us \u2014 open the shopping assistant');
    fab.setAttribute('aria-expanded', 'false');
    fab.innerHTML =
      '<svg class="kpc-ico-chat" viewBox="0 0 24 24" width="23" height="23" aria-hidden="true">' +
      '<path fill="currentColor" d="M12 3c5 0 9 3.4 9 7.6 0 4.2-4 7.6-9 7.6-.9 0-1.8-.1-2.6-.3L5 20.5l.8-3.3C4.1 15.8 3 13.4 3 10.6 3 6.4 7 3 12 3z"/></svg>' +
      '<span>Ask us</span>';
    /* DWELL NUDGE. After 20s on the page with the panel still shut, the
       launcher grows a small "Need help choosing?" bubble. This replaces a red
       "1" badge from the redesign, which asserted an unread message existed
       when none did. The nudge says what is actually on offer, which is both
       true and a better reason to tap. Fires once; open() clears it. */
    setTimeout(function () {
      if (fab && fab.getAttribute('aria-expanded') === 'false') {
        fab.setAttribute('data-notify', 'Need help?');
      }
    }, 20000);
    fab.addEventListener('click', function () {
      if (els && !els.panel.hidden) close(); else open();
    });
    /* CLS FIX (7 Aug 2026). The launcher used to be appended at full width and
       then resized to the 44px circle when the first queueLift() landed 60ms
       later. That resize happened AFTER the first paint, so it scored as a
       layout shift — Lighthouse costed it at 0.010 on the mobile PDP.

       Hidden until its final size is known. visibility (not display) so the
       element still has geometry for updateLift() to measure, and the whole
       decide-then-reveal runs in ONE task, so the browser never paints the
       intermediate state — there is no flash and no shift. */
    fab.style.visibility = 'hidden';
    document.body.appendChild(fab);
    updateLift();
    fab.style.visibility = '';

    addEventListener('scroll', queueLift, { passive: true });
    addEventListener('resize', queueLift, { passive: true });
    /* Bars that slide in with a CSS transition finish moving without any
       scroll/resize/class change — the signature guard cannot see the end of
       the animation, so re-test whenever one completes. Capture phase:
       transitionend does not bubble from every element. */
    document.addEventListener('transitionend', queueLift, true);
    /* Sticky ATC toggles .visible via its own script and the bottom nav can
       hide/show without a resize — poll cheaply as a safety net. */
    setInterval(queueLift, 800);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
